import React, { JSX, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, CardTitle, CardFooter } from 'reactstrap';
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  getFilteredRowModel,
  ColumnFilter,
  SortingState,
  Header,
  Cell,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import Pagination from './Pagination';
import DebouncedInput from '@/components/DebounceInput';
import Paginator from '@/pages/common/paginator';

// Add a type for the alignment options
export enum ColumnAlignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}
export interface ColumnMeta {
  alignment?: ColumnAlignment;
  className?: string;
}

export const getAlignment = (alignment?: ColumnAlignment) => {
  const alignmentMap = {
    [ColumnAlignment.LEFT]: 'left',
    [ColumnAlignment.CENTER]: 'center',
    [ColumnAlignment.RIGHT]: 'right',
  };

  return alignmentMap[alignment || ColumnAlignment.LEFT];
};

interface DataTableProps {
  data: any[];
  columns: ColumnDef<any, any>[];
  route?: string;
  pageSize?: number;
  onRowClick?: (row: any) => void;
  title?: string;
  onSearch?: (value: string | number) => void;
  columnFilters?: ColumnFilter[];
  sorting?: SortingState;
  enableSorting?: boolean;
  onSortingChange?: any;
  headerExtra?: React.ReactNode;
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  route,
  pageSize = 10,
  onRowClick,
  title,
  onSearch,
  columnFilters = undefined,
  sorting = undefined,
  enableSorting = true,
  onSortingChange = undefined,
  headerExtra = undefined,
}) => {
  const navigate = useNavigate();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnFilters,
      sorting,
    },
    onSortingChange,
    filterFns: {
      inRange: (row, columnId, { min, max }) => {
        const value = row.original[columnId];
        if (!max) {
          return value >= Number(min);
        }
        if (!min) {
          return value <= Number(max);
        }
        return value >= Number(min) && value <= Number(max);
      },
      inDateRange: (row, columnId, { min, max }) => {
        const value = new Date(row.original[columnId]);
        if (!max) {
          return value >= new Date(min);
        }
        if (!min) {
          return value <= new Date(max);
        }
        return value >= new Date(min) && value <= new Date(max);
      },
      inArray: (row, columnId, filterValue: string[]) => {
        if (!filterValue || filterValue.length === 0) return true;
        const value = row.original[columnId];
        return filterValue.includes(value);
      },
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
    enableSorting,
  });

  const handleRowClick = (row: any) => {
    if (row.id && route) {
      navigate(`${route}/${row.id}`);
    }
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const pageCount = table.getPageCount();

  const renderHeader = () => {
    if (!title && !onSearch && !headerExtra) {
      return null;
    }

    return (
      <CardHeader className="d-flex justify-content-between align-items-center">
        {title && (
          <CardTitle className="mb-0">
            <h3 className="mb-0">{title}</h3>
          </CardTitle>
        )}
        <div className="d-flex align-items-center gap-2">
          {onSearch && (
            <DebouncedInput
              className="form-control w-auto"
              placeholder="Search"
              isLoading={false}
              onChange={onSearch}
            />
          )}
          {headerExtra}
        </div>
      </CardHeader>
    );
  };

  return (
    <Card>
      {renderHeader && renderHeader()}
      <CardBody className="overflow-visible">
        <table className="table-hover table-centered mb-3 table overflow-auto align-middle">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeader key={header.id} header={header} />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleRowClick(row.original)}
                style={{ cursor: onRowClick ? 'pointer' : '' }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} cell={cell} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
      {pageCount > 1 && (
        <CardFooter>
          <Pagination table={table} />
        </CardFooter>
      )}
    </Card>
  );
};

export default DataTable;

interface TableCellProps<T extends object> {
  cell: Cell<T, unknown>;
}

function TableCell<T extends object>({ cell }: TableCellProps<T>): JSX.Element {
  const meta = cell.column.columnDef.meta as ColumnMeta;
  const alignment = getAlignment(meta?.alignment);

  return (
    <td
      style={{ width: cell.column.getSize(), maxWidth: cell.column.getSize(), textAlign: alignment as any }}
      className={`text-truncate overflow-visible ${meta?.className || ''}`}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
}

interface TableHeaderProps<T extends object> {
  header: Header<T, unknown>;
}
function TableHeader<T extends object>({ header }: TableHeaderProps<T>): JSX.Element {
  const isSortable = header.column.columnDef.enableSorting;

  // Memoize className based on meta configuration
  // Memoize className based on meta configuration
  const alignment = useMemo(() => {
    const meta = header.column.columnDef.meta as ColumnMeta;
    return getAlignment(meta?.alignment);
  }, [header.column.columnDef.meta, isSortable]);
  const headerClassName = useMemo(() => {
    const meta = header.column.columnDef.meta as ColumnMeta;
    return meta?.className || '';
  }, [header.column.columnDef.meta]);

  return (
    <th
      key={header.id}
      onClick={header.column.getToggleSortingHandler()}
      className={`cursor-pointer ${headerClassName}`}
      style={{ width: header.getSize(), textAlign: alignment as any }}
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
      {header.column.getCanSort() ? (
        header.column.getIsSorted() ? (
          header.column.getIsSorted() === 'desc' ? (
            <i className="ri-arrow-down-s-line" />
          ) : (
            <i className="ri-arrow-up-s-line" />
          )
        ) : (
          <i className="ri-arrow-up-down-line ms-1" />
        )
      ) : null}
    </th>
  );
}
